/**
 * A creature's portrait, `?portrait=skogstroll`: a small run that has just ended, with the
 * creature risen out of its gap in a scene of its own, staged like one of John Bauer's
 * illustrations, for the picture on the card after death. `&model=jatte-1` puts another creature in
 * the same scene. For art direction; players see the picture, never this view.
 *
 * The camera looks from +x +y: around the creature's tile, f = dx + dy is below 0 behind it and
 * above 0 in front, and s = dx - dy runs from the left of the picture to the right. Tall things
 * stand behind and at the sides, as Bauer's trunks do; only low things stand in front, and the
 * ground ends one tile in front of the creature, where the mist begins.
 */
import { type Coord, mix32, Terrain } from "@mistsweeper/core";
import { type Placement, SWAY, type Sway, type WorldSource } from "@mistsweeper/renderer";

/** How far the ground reaches from the creature to the sides and back, in tiles. */
const REACH = 6;
/** How far it reaches in front. */
const FRONT = 1;
const WATER = -0.1;
const SALT = 0x706f7274; // "port"

/** [dx, dy, model, offset x, offset y, yaw, scale, height] from the creature's tile. */
type Spot = readonly [number, number, string, number, number, number, number, number?];
type Fill = (f: number, s: number, rand: () => number) => readonly Spot[];

interface Scene {
  readonly model: string;
  readonly ground: (f: number, s: number) => Terrain;
  readonly spots: readonly Spot[];
  readonly fill: Fill;
}

const SPRUCES = [
  "iso-spruce-5",
  "iso-spruce-7",
  "iso-spruce-8",
  "iso-pine-1",
  "iso-pine-2",
  "iso-spruce-6",
];
const BIRCHES = ["iso-birch-1", "iso-birch-2", "iso-birch-3", "iso-birch-4"];
const LOW = ["iso-fern-1", "iso-fern-2", "iso-tuft-1", "iso-tuft-2", "iso-tuft-4", "iso-pebbles-1"];

const one = <T>(list: readonly T[], rand: () => number): T =>
  list[Math.floor(rand() * list.length)] as T;

/** Trees at a spot: n of them from `kinds`, spread over the tile, turned and sized at random. */
function trees(kinds: readonly string[], n: number, rand: () => number): Spot[] {
  const out: Spot[] = [];
  for (let i = 0; i < n; i += 1) {
    out.push([
      0,
      0,
      one(kinds, rand),
      rand() * 0.6 - 0.3,
      rand() * 0.6 - 0.3,
      rand() * 6.28,
      0.85 + rand() * 0.3,
    ]);
  }
  return out;
}

/** The forest behind and beside the creature, thick, and low growth in front. */
const forest =
  (kinds: readonly string[], under: number): Fill =>
  (f, s, rand) => {
    if (f > 0)
      return rand() < 0.5
        ? [[0, 0, one(LOW, rand), rand() * 0.4 - 0.2, rand() * 0.4 - 0.2, rand() * 6.28, 1]]
        : [];
    if (f === 0 && Math.abs(s) < 2) return [];
    if (f === -1 && Math.abs(s) < 1) return [];
    const out = trees(kinds, f <= -3 ? 2 : 1 + Math.floor(rand() * 2), rand);
    if (rand() < under)
      out.push([0, 0, one(LOW, rand), rand() * 0.6 - 0.3, rand() * 0.6 - 0.3, rand() * 6.28, 1]);
    return out;
  };

/** Across the water, the far shore: a forest from two tiles behind the creature. */
const SHORE = -2;
const shore = (f: number) => (f <= SHORE ? Terrain.forest : Terrain.water);
const farShore: Fill = (f, s, rand) => {
  if (f > SHORE)
    return f === SHORE + 1 && rand() < 0.5
      ? [[0, 0, "iso-reeds-1", rand() * 0.4 - 0.2, -0.3, rand() * 6.28, 1, WATER]]
      : [];
  if (f === SHORE && Math.abs(s) < 4 && rand() < 0.4) return [];
  return trees(
    f === SHORE ? ["iso-birch-3", "iso-spruce-6", "iso-spruce-8"] : SPRUCES,
    f <= SHORE - 1 ? 2 : 1,
    rand,
  );
};

const SCENES: Record<string, Scene> = {
  skogstroll: {
    model: "skogstroll-1",
    ground: () => Terrain.forest,
    fill: forest(SPRUCES, 0.3),
    spots: [
      [-1, -1, "iso-spruce-5", -0.15, -0.15, 0.3, 1],
      [-1, 1, "iso-spruce-8", -0.25, 0.25, 0.9, 1],
      [1, -1, "iso-pine-3", 0.25, -0.25, 2.2, 1],
      [1, 0, "iso-fern-1", 0.1, 0.2, 0.3, 1],
      [1, 1, "iso-stump-1", -0.1, -0.15, 0.6, 1],
      [0, 1, "iso-deer-1", 0.0, 0.12, 0.8, 1],
      [-1, 1, "iso-tuft-2", 0.3, 0.3, 1.0, 1],
    ],
  },
  huldra: {
    model: "huldra-1",
    ground: () => Terrain.forest,
    fill: forest([...BIRCHES, ...BIRCHES, "iso-spruce-5", "iso-spruce-8"], 0.6),
    spots: [
      [-1, -1, "iso-birch-4", -0.2, -0.1, 0.4, 1.1],
      [1, 0, "iso-fern-2", 0.1, 0.15, 0.3, 1.2],
      [0, 1, "iso-fern-1", -0.1, 0.1, 1.6, 1.2],
      [1, 1, "iso-pebbles-2", 0, 0, 0, 1],
    ],
  },
  bergstroll: {
    model: "bergstroll-1",
    ground: () => Terrain.mountain,
    fill: (f, s, rand) => {
      if (f > 0) return rand() < 0.4 ? [[0, 0, "iso-pebbles-3", 0, 0, rand() * 6.28, 1]] : [];
      if (f >= -1 && Math.abs(s) < 2) return [];
      if (f <= -3 && (f + s) % 2 === 0)
        return [
          [0, 0, one(["iso-peak-1", "iso-peak-2"], rand), 0, 0, rand() * 6.28, 0.9 + rand() * 0.4],
        ];
      const v = rand();
      if (v < 0.35)
        return [
          [
            0,
            0,
            one(["iso-outcrop-1", "iso-outcrop-2", "iso-rock-1", "iso-rock-2"], rand),
            0,
            0,
            rand() * 6.28,
            1,
          ],
        ];
      if (v < 0.6) return trees(["iso-pine-1", "iso-pine-2", "iso-spruce-6"], 1, rand);
      return [];
    },
    spots: [
      [-1, 1, "iso-cairn-1", 0.1, 0.2, 0.5, 1],
      [1, -1, "iso-boulder-2", 0.3, -0.2, 1.0, 0.8],
      [-2, 0, "iso-runestone-2", 0, 0, 0.6, 1],
    ],
  },
  jatte: {
    model: "jatte-2",
    ground: () => Terrain.grass,
    fill: (f, s, rand) => {
      if (f > 0)
        return rand() < 0.6
          ? [[0, 0, one(["iso-tuft-1", "iso-tuft-3", "iso-tuft-5"], rand), 0, 0, rand() * 6.28, 1]]
          : [];
      if (f >= -1 && Math.abs(s) < 2) return [];
      const v = rand();
      if (f <= -6) return trees(SPRUCES, 2, rand);
      if (v < 0.25) return trees(BIRCHES, 1, rand);
      if (v < 0.45)
        return [
          [
            0,
            0,
            one(["iso-juniper-1", "iso-juniper-2", "iso-bush-1", "iso-bush-2"], rand),
            0,
            0,
            rand() * 6.28,
            1,
          ],
        ];
      if (v < 0.75)
        return [
          [0, 0, one(["iso-tuft-1", "iso-tuft-3", "iso-tuft-5"], rand), 0, 0, rand() * 6.28, 1],
        ];
      return [];
    },
    spots: [
      [-3, -2, "iso-church-2", 0, 0, 0.8, 1],
      [1, 0, "iso-boulder-1", 0.1, 0.1, 0.3, 1],
      [-1, 1, "iso-fence-1", 0, 0.1, 0.8, 1],
    ],
  },
  nacken: {
    model: "nacken-1",
    ground: shore,
    fill: farShore,
    spots: [
      [-1, 0, "iso-jetty-1", 0.05, 0, 0, 1.2, WATER],
      [-1, 1, "iso-boat-1", 0.1, -0.1, 1.4, 1, WATER],
      [0, -1, "iso-reeds-2", 0.1, -0.25, 1, 1, WATER],
      [-2, 1, "iso-reeds-1", 0.2, -0.2, 2, 1, WATER],
      [1, -1, "iso-reeds-1", 0.25, -0.1, 0.5, 1, WATER],
      [1, 0, "iso-lilypads-2", 0, 0.1, 0, 1, WATER],
      [0, 1, "iso-lilypads-1", -0.1, 0, 1, 1, WATER],
      [-2, 0, "iso-spruce-6", -0.2, -0.2, 0.3, 1],
    ],
  },
  sjotroll: {
    model: "sjotroll-2",
    ground: shore,
    fill: farShore,
    spots: [
      [-2, 1, "iso-boathouse-1", 0.1, -0.1, 0, 1, WATER],
      [1, -1, "iso-boat-1", 0, 0.1, 2.2, 1, WATER],
      [0, -1, "iso-reeds-2", 0.1, -0.3, 0, 1, WATER],
      [-1, 0, "iso-reeds-1", -0.3, 0.2, 1, 1, WATER],
      [1, 0, "iso-lilypads-1", 0, 0.1, 0, 1, WATER],
      [-1, 1, "iso-lilypads-2", 0.2, 0.2, 2, 1, WATER],
    ],
  },
};

export interface Portrait {
  readonly world: WorldSource;
  readonly dress: (x: number, y: number) => Placement[] | null;
  readonly centre: Coord;
  /** The creature risen in the gap, or null for a banner's picture. */
  readonly model: string | null;
}

/**
 * A banner's picture for the Supporter page (decision 0035), `?portrait=banner&banner=b2`: the
 * banner alone, proven on its tile of grass and so standing in its cairn, the mist round it, by
 * `scripts/banner-pictures.mjs`. The renderer plants the flag `&banner=` names.
 */
function bannerPortrait(): Portrait {
  const centre = { x: 40, y: 40 };
  const at = (x: number, y: number) => x === centre.x && y === centre.y;
  const world: WorldSource = {
    isRevealed: () => false,
    hasBanner: at,
    adjacent: () => 0,
    terrain: () => Terrain.grass,
    isChasm: at,
    isClosed: () => false,
    isSettled: at,
    status: "playing",
    killer: null,
  };
  return { world, dress: () => null, centre, model: null };
}

function swayOf(model: string): Sway {
  if (/tuft|fern|reeds/.test(model)) return SWAY.grass;
  if (/spruce|pine|birch|juniper|bush/.test(model)) return SWAY.tree;
  return SWAY.none;
}

export function makePortrait(params: URLSearchParams): Portrait | null {
  if (params.get("portrait") === "banner") return bannerPortrait();
  const scene = SCENES[params.get("portrait") ?? ""];
  if (!scene) return null;
  const model = params.get("model") ?? scene.model;
  if (!/^[a-z]+-\d$/.test(model)) return null;
  const centre = { x: 40, y: 40 };
  const near = (x: number, y: number) => {
    const dx = x - centre.x;
    const dy = y - centre.y;
    return Math.abs(dx) <= REACH && Math.abs(dy) <= REACH && dx + dy <= FRONT;
  };
  const gap = (x: number, y: number) => x === centre.x && y === centre.y;
  const world: WorldSource = {
    isRevealed: (x, y) => near(x, y) && !gap(x, y),
    hasBanner: () => false,
    adjacent: () => 0,
    // In front of the staged ground lies open grass, so no silhouette in the mist stands between
    // the camera and the creature.
    terrain: (x, y) =>
      x - centre.x + (y - centre.y) > FRONT
        ? Terrain.grass
        : scene.ground(x - centre.x + (y - centre.y), x - centre.x - (y - centre.y)),
    isChasm: gap,
    isClosed: () => false,
    isSettled: () => false,
    status: "dead",
    killer: centre,
  };
  const spots = new Map<string, Placement[]>();
  const add = (x: number, y: number, [, , name, ox, oy, yaw, scale, h]: Spot) => {
    const k = `${x},${y}`;
    const placement: Placement = {
      name,
      dx: ox,
      dy: oy,
      yaw,
      scale,
      sway: swayOf(name),
      ...(h === undefined ? {} : { h }),
    };
    spots.set(k, [...(spots.get(k) ?? []), placement]);
  };
  const handPlaced = new Set(scene.spots.map(([dx, dy]) => `${centre.x + dx},${centre.y + dy}`));
  for (let dy = -REACH; dy <= REACH; dy += 1) {
    for (let dx = -REACH; dx <= REACH; dx += 1) {
      const x = centre.x + dx;
      const y = centre.y + dy;
      if (!near(x, y) || gap(x, y) || handPlaced.has(`${x},${y}`)) continue;
      let n = 0;
      const rand = () => {
        n += 1;
        return mix32(dx * 7919 + dy, n, SALT) / 4294967296;
      };
      for (const spot of scene.fill(dx + dy, dx - dy, rand)) add(x, y, spot);
    }
  }
  for (const spot of scene.spots) add(centre.x + spot[0], centre.y + spot[1], spot);
  return {
    world,
    dress: (x, y) => spots.get(`${x},${y}`) ?? null,
    centre,
    model: `iso-${model}`,
  };
}
